import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Switch, Route, useRouteMatch, useLocation, useHistory, Link, Redirect,
} from 'react-router-dom';
import {
  Table, Spin, Tabs, Tag, Badge, Alert,
} from 'antd';
import './SurveyScreen.css';
import {
  FormScreen, StatesSummary, StatesList, StateDetail,
} from '..';
import { groupBy } from '../../helpers';
import { CreateBtn } from '../../components/UI';
import fetchExportsBySurvey from '../../services/api/fetchExportsBySurvey';
import useSurveyHealth from './useSurveyHealth';
import { hasActionFindings, destToUrl } from '../StatesExplorer/healthNav';

const { TabPane } = Tabs;

const Survey = ({ forms, selected }) => {
  const nameLookup = Object.fromEntries(forms.map(f => [f.id, f.prettyName]));

  const match = useRouteMatch();
  const getTranslationInfo = (record) => {
    if (record.translation_conf.self) {
      return 'self';
    }
    const dest = record.translation_conf.destination;
    if (dest && nameLookup[dest]) {
      return nameLookup[dest];
    }

    return null;
  };

  const grouped = groupBy(forms, f => f.shortcode);
  const data = [...grouped].map(([__, forms]) => forms[0]);
  const metadataFields = forms
    .map(f => f.metadata)
    .filter(md => md)
    .reduce((a, b) => [...a, ...Object.keys(b).filter(k => !a.includes(k))], []);

  let columns = ['shortcode', 'version', 'created', ...metadataFields]
    .map(f => ({ title: f, dataIndex: f, sorter: { compare: (a, b) => (a[f] > b[f] ? 1 : -1) } }));

  const ShortCodeLink = (text, record) => (
    <Link to={`${match.url}/form/${record.id}`}>
      {' '}
      {text}
      {' '}
    </Link>
  );

  columns[0] = {
    ...columns[0],
    render: ShortCodeLink,
  };

  columns[2] = {
    ...columns[2],
    render: text => (`${text.toLocaleDateString()} - ${text.toLocaleTimeString()}`),
  };

  const ActionLink = (text, record) => (<Link to={`/surveys/create?from=${record.id}`}> new version </Link>);

  columns = [...columns,
    { title: 'translation', dataIndex: 'translation_conf', render: (text, record) => getTranslationInfo(record) },
    { title: 'actions', dataIndex: 'id', render: ActionLink },
    { title: 'killed', dataIndex: 'off_time', render: text => text && (<span className="skull">☠☠☠</span>) },
  ];

  const PrettyNameLink = (text, record) => (
    <Link to={`${match.url}/form/${record.id}`}>
      {record.prettyName}
    </Link>
  );

  const expandedRowRender = (row) => {
    const expanded = grouped.get(row.shortcode);
    const cols = [...columns];
    cols[0] = {
      title: 'form',
      dataIndex: 'prettyName',
      render: PrettyNameLink,
    };

    return (<Table columns={cols} dataSource={expanded} pagination={false} showHeader />);
  };


  return (
    <Spin spinning={false}>
      <div className="survey-table">
        <div className="buttons">
          <CreateBtn to={`/surveys/create?survey_name=${encodeURIComponent(selected)}`}> NEW FORM </CreateBtn>
        </div>
        <Table
          columns={columns}
          dataSource={data}
          pagination={{ pageSize: 20 }}
          expandable={{ expandedRowRender, indentSize: 100 }}
        />
      </div>
    </Spin>
  );
};

const DownloadLink = text => (
  text && text !== 'Not Found'
    ? <a href={text} target="_blank" rel="noopener noreferrer">DOWNLOAD</a>
    : null
);

// Layer-2 banners: render only when firing (silent by default). Platform
// notices are calm/informational — not caused by the researcher's
// configuration. Study `action` findings get an amber banner; multiple
// findings collapse into a single "N issues" line rather than stacking.
const HealthBanners = ({ health, monitorUrl }) => {
  const notices = (health && health.notices) || [];
  const actions = ((health && health.findings) || []).filter(
    f => f.level === 'action',
  );

  return (
    <div>
      {notices.map(notice => (
        <Alert
          key={notice.alertname}
          type="info"
          showIcon
          message={notice.message}
          style={{ marginBottom: 12 }}
        />
      ))}
      {actions.length === 1 && (
        <Alert
          type="warning"
          showIcon
          message={actions[0].message}
          action={
            destToUrl(actions[0].action, monitorUrl) && (
              <Link to={destToUrl(actions[0].action, monitorUrl)}>
                {actions[0].action.label}
              </Link>
            )
          }
          style={{ marginBottom: 12 }}
        />
      )}
      {actions.length > 1 && (
        <Alert
          type="warning"
          showIcon
          message={`${actions.length} issues need attention — see Health below`}
          style={{ marginBottom: 12 }}
        />
      )}
    </div>
  );
};

const MonitorSection = ({ surveyName, match, health }) => {
  const location = useLocation();
  const history = useHistory();

  const getActiveSubTab = () => {
    const path = location.pathname;
    if (path.endsWith('/list') || path.includes('/list?')) return 'list';
    // Detail pages (monitor/:userid) should highlight the "list" tab
    const monitorBase = `${match.url}/monitor`;
    const remainder = path.slice(monitorBase.length);
    if (remainder && remainder !== '/' && !remainder.startsWith('/list')) return 'list';
    return 'summary';
  };

  const handleSubTabChange = (key) => {
    if (key === 'summary') {
      history.push(`${match.url}/monitor`);
    } else {
      history.push(`${match.url}/monitor/list`);
    }
  };

  return (
    <div>
      <HealthBanners health={health} monitorUrl={`${match.url}/monitor`} />

      <Tabs
        activeKey={getActiveSubTab()}
        onChange={handleSubTabChange}
        size="small"
        style={{ marginBottom: 16 }}
      >
        <TabPane tab="Summary" key="summary" />
        <TabPane tab="Respondents" key="list" />
      </Tabs>

      <Switch>
        <Route exact path={`${match.path}/monitor`}>
          <StatesSummary surveyName={surveyName} health={health} />
        </Route>
        <Route exact path={`${match.path}/monitor/list`}>
          <StatesList surveyName={surveyName} />
        </Route>
        <Route exact path={`${match.path}/monitor/:userid`}>
          <StateDetail surveyName={surveyName} backPath={`${match.url}/monitor/list`} />
        </Route>
      </Switch>
    </div>
  );
};

const STALE_MS = 4 * 60 * 60 * 1000; // 4 hours

const isStale = updated => Date.now() - new Date(updated).getTime() > STALE_MS;

const IN_PROGRESS_STATUSES = [
  'Requested', 'Processing',
  'Querying', 'Formatting', 'Writing', 'Uploading',
  'Started', // legacy — pre-v0.6.6 exports may still carry this
];
const isInProgress = status => IN_PROGRESS_STATUSES.includes(status);

const renderMetadata = (metadata) => {
  if (!metadata || typeof metadata !== 'object') return null;
  const entries = Object.entries(metadata);
  if (entries.length === 0) return null;
  return (
    <span>
      {entries.map(([k, v]) => (
        <Tag key={k} color={k === 'error' ? 'red' : 'blue'} style={{ marginBottom: 4 }}>
          {k === 'error' ? v : `${k}: ${typeof v === 'number' ? v.toLocaleString() : v}`}
        </Tag>
      ))}
    </span>
  );
};

const exportColumns = [
  { title: 'Source', dataIndex: 'source', render: text => ({ chat_log: 'Chat Log', full_messages: 'Full Messages' }[text] || 'Responses') },
  {
    title: 'Status',
    dataIndex: 'status',
    render: (status, record) => {
      if (isInProgress(status)) {
        return isStale(record.updated)
          ? <span style={{ color: '#faad14' }}>Stale</span>
          : status;
      }
      if (status === 'Failed') {
        return <span style={{ color: '#ff4d4f' }}>Failed</span>;
      }
      return status;
    },
  },
  { title: 'Details', dataIndex: 'metadata', render: renderMetadata },
  { title: 'Time', dataIndex: 'updated' },
  {
    title: 'Download',
    dataIndex: 'export_link',
    render: (text, record) => (
      isInProgress(record.status) && !isStale(record.updated)
        ? <Spin size="small" />
        : DownloadLink(text)
    ),
  },
];

const ExportPanel = ({ selected }) => {
  const [exports, setExports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let intervalId = null;

    const fetchExports = () => {
      fetchExportsBySurvey(selected)
        .then((data) => {
          if (!cancelled) {
            const rows = data || [];
            setExports(rows);
            // Stop polling once no active (non-stale) exports remain
            if (intervalId && !rows.some(r => isInProgress(r.status) && !isStale(r.updated))) {
              clearInterval(intervalId);
              intervalId = null;
            }
          }
        })
        .catch(err => console.error(err))
        .finally(() => { if (!cancelled) setLoading(false); });
    };

    fetchExports();
    intervalId = setInterval(fetchExports, 4000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [selected]);

  return (
    <div style={{ padding: '24px 0' }}>
      <div style={{ marginBottom: 24 }}>
        <CreateBtn to={`/exports/create?survey_name=${encodeURIComponent(selected)}`}>
          EXPORT RESPONSES
        </CreateBtn>
        <CreateBtn to={`/exports/create-chat-log?survey_name=${encodeURIComponent(selected)}`} style={{ marginLeft: 16 }}>
          EXPORT CHAT LOG
        </CreateBtn>
        <CreateBtn to={`/exports/create-full-messages?survey_name=${encodeURIComponent(selected)}`} style={{ marginLeft: 16 }}>
          EXPORT FULL MESSAGES
        </CreateBtn>
      </div>

      <Spin spinning={loading}>
        <Table
          columns={exportColumns}
          dataSource={exports}
          rowKey="id"
          pagination={{ pageSize: 10 }}
        />
      </Spin>
    </div>
  );
};

const SurveyScreen = ({ forms, selected }) => {
  const match = useRouteMatch();
  const location = useLocation();
  const history = useHistory();
  const health = useSurveyHealth(selected);

  const getActiveTab = () => {
    const path = location.pathname.slice(match.url.length);
    if (path.startsWith('/monitor')) return 'monitor';
    if (path.startsWith('/export')) return 'export';
    return 'edit';
  };

  const handleTabChange = (key) => {
    history.push(`${match.url}/${key}`);
  };

  // Layer-1 ambient badge: amber dot on the Monitor tab when any `action`
  // finding is live. Absent when healthy — zero pixels when fine.
  const monitorTab = hasActionFindings(health.findings) ? (
    <Badge dot offset={[6, 0]} style={{ backgroundColor: '#faad14' }}>
      Monitor
    </Badge>
  ) : (
    'Monitor'
  );

  return (
    <div>
      <Tabs activeKey={getActiveTab()} onChange={handleTabChange}>
        <TabPane tab="Edit" key="edit" />
        <TabPane tab={monitorTab} key="monitor" />
        <TabPane tab="Export" key="export" />
      </Tabs>

      <Switch>
        <Redirect exact from={match.path} to={`${match.url}/edit`} />

        <Route exact path={`${match.path}/edit`}>
          <Survey forms={forms} selected={selected} />
        </Route>
        <Route exact path={`${match.path}/edit/form/:surveyid`}>
          <FormScreen forms={forms} />
        </Route>

        <Route path={`${match.path}/monitor`}>
          <MonitorSection surveyName={selected} match={match} health={health} />
        </Route>

        <Route exact path={`${match.path}/export`}>
          <ExportPanel selected={selected} />
        </Route>
      </Switch>
    </div>
  );
};

Survey.propTypes = {
  forms: PropTypes.arrayOf(PropTypes.object).isRequired,
  selected: PropTypes.string.isRequired,
};

ExportPanel.propTypes = {
  selected: PropTypes.string.isRequired,
};

const healthShape = PropTypes.shape({
  findings: PropTypes.arrayOf(PropTypes.object),
  notices: PropTypes.arrayOf(PropTypes.object),
});

HealthBanners.propTypes = {
  health: healthShape,
  monitorUrl: PropTypes.string.isRequired,
};

HealthBanners.defaultProps = {
  health: null,
};

MonitorSection.propTypes = {
  surveyName: PropTypes.string.isRequired,
  match: PropTypes.object.isRequired,
  health: healthShape,
};

MonitorSection.defaultProps = {
  health: null,
};

SurveyScreen.propTypes = {
  forms: PropTypes.arrayOf(PropTypes.object).isRequired,
  selected: PropTypes.string.isRequired,
};

export default SurveyScreen;
